import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";

import { ConfiguratorTypes } from "../../../constants";
import { IProduct } from "../../../models/catalogue";
import { IImageURL, isoImageURL } from "../../../models/nominals";
import { IProductShippingMethod } from "../../../models/shipping";
import { useAppSelector } from "../../../store/reducers";
import { ConfiguratorFreeDelivery } from "../ConfiguratorFreeDelivery";
import { FreeDeliveryButtonTypes } from "../constants";
import { defaults } from "../defaults";
import { isPreorderProduct } from "../utils";

import styles from "./index.module.scss";

function getAnalyticsClass(shippingDescription: string): string {
    let analyticsClass = "";
    // TODO(i18n): String matching assumes English text and will fail for translated content. Consider using structured data (e.g., shipping tier codes) instead of parsing translated strings.
    if (shippingDescription.includes("1-2 weeks")) {
        analyticsClass = "pdploaded-1to2weeks";
    }
    // TODO(i18n): String matching assumes English text and will fail for translated content. Consider using structured data (e.g., shipping tier codes) instead of parsing translated strings.
    if (shippingDescription.includes("5-7 days")) {
        analyticsClass = "pdploaded-5to7days";
    }
    // TODO(i18n): String matching assumes English text and will fail for translated content. Consider using structured data (e.g., shipping tier codes) instead of parsing translated strings.
    if (shippingDescription.includes("3-5 days")) {
        analyticsClass = "pdploaded-3to5days";
    }
    return analyticsClass;
}

function getShippingDescr(
    variantIsAvailableToBuy: boolean,
    upgradedVariant: IProduct | null,
    rootProduct: IProduct | null,
    getDeliveryCopy:
        | ((rootProduct: IProduct, isPreorder: boolean) => string | null)
        | undefined,
    shippingMethod: IProductShippingMethod | null,
    showShippingLead: boolean | undefined,
): string {
    if (
        !variantIsAvailableToBuy ||
        !upgradedVariant ||
        !rootProduct ||
        !getDeliveryCopy
    ) {
        return "";
    }

    const deliveryCopyPreorder = getDeliveryCopy(rootProduct, true);
    const deliveryCopyNormal = getDeliveryCopy(rootProduct, false);

    // If this is a preorder product AND the delivery copy function didnt return an empty string,
    // set the shipping description to the function's result
    if (isPreorderProduct(upgradedVariant) && deliveryCopyPreorder) {
        return deliveryCopyPreorder;
    }

    const storedDescription = shippingMethod
        ? shippingMethod.description
        : null;
    // If we still don't have a shipping description and the CMS has showShippingLead enabled AND there was a shipping description passed in from the CMS,
    // set the shipping description to that CMS field
    if (showShippingLead && storedDescription) {
        return storedDescription;
    } else {
        // Otherwise, if the delivery copy function for non-preorder didnt return an empty string,
        // set the shipping description to that function's result
        if (deliveryCopyNormal) {
            return deliveryCopyNormal;
        } else {
            return "";
        }
    }
}

function getShippingIcon(
    getDeliveryIcon: (() => IImageURL | null) | undefined,
    altText: string,
): React.ReactNode {
    if (!getDeliveryIcon) {
        return null;
    }

    const iconURL = getDeliveryIcon();
    if (!iconURL) {
        return null;
    }

    return (
        <img
            className={styles.icon}
            src={isoImageURL.unwrap(iconURL)}
            alt={altText}
        />
    );
}

export const ConfiguratorDeliveryDisclaimer = (
    props: Immutable<{
        configuratorType: ConfiguratorTypes;
        variantIsAvailableToBuy: boolean;
        rootProduct: IProduct | null;
        upgradedVariant: IProduct | null;
        showShippingLead?: boolean;
        phoneNumber?: string;
        getDeliveryCopy?: (
            rootProduct: IProduct,
            isPreorder: boolean,
        ) => string | null;
        getDeliveryIcon?: () => IImageURL | null;
    }>,
): React.ReactNode => {
    const shippingMethod = useAppSelector(
        (rootState) => (rootState.configurator || defaults).shippingMethod,
    );

    const description = getShippingDescr(
        props.variantIsAvailableToBuy,
        props.upgradedVariant,
        props.rootProduct,
        props.getDeliveryCopy,
        shippingMethod,
        props.showShippingLead,
    );
    const analyticsClass = getAnalyticsClass(description);
    const layoutDesign = props.configuratorType;
    const disclaimerClasses = classNames({
        "configurator__delivery-disclaimer": true,
        [`configurator__delivery-disclaimer--${layoutDesign}`]: true,
        [styles.root]: true,
        [styles.updated]: layoutDesign === ConfiguratorTypes.UPDATED,
        [`al-${analyticsClass}`]: true,
    });

    if (!description) {
        return null;
    }

    return (
        <div className={disclaimerClasses}>
            <ConfiguratorFreeDelivery
                upgradedVariant={props.upgradedVariant}
                variantIsAvailableToBuy={props.variantIsAvailableToBuy}
                freeDeliveryButtonTypes={FreeDeliveryButtonTypes.TEXT}
                configuratorType={layoutDesign}
                phoneNumber={props.phoneNumber}
            />
            <span>
                {getShippingIcon(props.getDeliveryIcon, description)}
                {description}
                <ConfiguratorFreeDelivery
                    upgradedVariant={props.upgradedVariant}
                    variantIsAvailableToBuy={props.variantIsAvailableToBuy}
                    freeDeliveryButtonTypes={FreeDeliveryButtonTypes.ICON}
                    configuratorType={layoutDesign}
                    phoneNumber={props.phoneNumber}
                />
            </span>
        </div>
    );
};
